import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Bell, Mail, Trash2, Plus, Send, CheckCircle2, AlertTriangle, Clock,
  RefreshCw, Server, Cloud, Inbox, XCircle, Zap,
} from "lucide-react";
import NotificationTemplatesPanel from "./NotificationTemplatesPanel";

export default function NotificationsPanel() {
  const [settings, setSettings] = useState(null);
  const [channel, setChannel] = useState(null);
  const [newEmail, setNewEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [adding, setAdding] = useState(false);
  const [replaying, setReplaying] = useState(false);
  const [expandedIdx, setExpandedIdx] = useState(null);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [s, c] = await Promise.all([
        axios.get(`${API}/admin/notification-settings`),
        axios.get(`${API}/admin/notification-channel-status?include_pending=true`),
      ]);
      setSettings(s.data);
      setChannel(c.data);
      // Broadcast so the sidebar Bildirimler badge refreshes immediately.
      window.dispatchEvent(new CustomEvent("kahu:notif-queue-changed"));
    } catch { toast.error("Veriler yüklenemedi"); }
    finally { setLoading(false); }
  };

  const addEmail = async () => {
    if (!newEmail.trim() || !newEmail.includes("@")) {
      toast.error("Geçerli bir e-posta girin");
      return;
    }
    setAdding(true);
    try {
      await axios.post(`${API}/admin/notification-settings/email`, { email: newEmail.trim() });
      toast.success(`${newEmail} eklendi`);
      setNewEmail("");
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Eklenemedi");
    } finally { setAdding(false); }
  };

  const removeEmail = async (email) => {
    try {
      await axios.delete(`${API}/admin/notification-settings/email/${encodeURIComponent(email)}`);
      toast.success(`${email} kaldırıldı`);
      fetchAll();
    } catch { toast.error("Silinemedi"); }
  };

  const toggleEmail = async (enabled) => {
    try {
      await axios.put(`${API}/admin/notification-settings`, { email_enabled: enabled });
      setSettings(prev => ({ ...prev, email_enabled: enabled }));
      toast.success(enabled ? "E-posta bildirimleri açıldı" : "E-posta bildirimleri kapatıldı");
    } catch { toast.error("Güncellenemedi"); }
  };

  const sendTest = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/admin/notification-settings/test`);
      toast.success("Test e-postası gönderildi");
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Test gönderilemedi");
    } finally { setSending(false); }
  };

  const replayQueue = async () => {
    if (!confirm("Kuyruktaki tüm bildirimleri göndermek istediğine emin misin?")) return;
    setReplaying(true);
    try {
      const res = await axios.post(`${API}/admin/notification-channel-replay`);
      toast.success(`${res.data.replayed} gönderildi${res.data.failed ? ", " + res.data.failed + " başarısız" : ""}`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Tekrar gönderim başarısız");
    } finally { setReplaying(false); }
  };

  const sendPendingItem = async (idx) => {
    try {
      await axios.post(`${API}/admin/notifications/pending/${idx}/send`);
      toast.success("Bildirim gönderildi");
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gönderim başarısız");
    }
  };

  const deletePendingItem = async (idx, subject) => {
    if (!confirm(`"${subject}" bildirimi silinsin mi?`)) return;
    try {
      await axios.delete(`${API}/admin/notifications/pending/${idx}`);
      toast.success("Silindi");
      fetchAll();
    } catch { toast.error("Silinemedi"); }
  };

  const clearQueue = async () => {
    if (!confirm("KUYRUKTAKİ TÜM BİLDİRİMLERİ kalıcı olarak sil? Bu işlem geri alınamaz.")) return;
    try {
      await axios.post(`${API}/admin/notifications/pending/clear`);
      toast.success("Kuyruk temizlendi");
      fetchAll();
    } catch { toast.error("Silinemedi"); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><div className="spinner" /></div>;

  const recipients = settings?.email_recipients || [];
  const logs = settings?.notification_log || [];
  const pendingQueue = channel?.pending_queue || [];
  const channelOn = channel?.email_channel_enabled;
  const smtpReady = channel?.smtp?.configured;
  const resendReady = channel?.resend?.configured && !channel?.resend?.is_sandbox;

  const typeLabel = (t) => ({
    test: "Test bildirimi",
    new_application: "Yeni başvuru",
    new_donation: "Yeni bağış",
    new_guardian: "Yeni koruyucu aile",
    replay: "Toplu gönderim",
    manual_send: "Manuel gönderim",
  }[t] || t || "Bildirim");

  return (
    <div className="space-y-6" data-testid="notifications-panel">
      <div>
        <h1 className="font-heading text-3xl">Bildirimler</h1>
        <p className="text-muted-foreground text-sm mt-1">E-posta kanalı durumu, kuyrukta bekleyen bildirimler ve alıcı yönetimi.</p>
      </div>

      {/* Channel Status — Hero Card */}
      <Card className={`border-2 ${channelOn ? "border-green-400 bg-green-50/50" : "border-amber-400 bg-amber-50/50"}`} data-testid="channel-status-card">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-start gap-3 flex-wrap">
            {channelOn ? <CheckCircle2 className="w-7 h-7 text-green-600 shrink-0 mt-1" /> : <AlertTriangle className="w-7 h-7 text-amber-600 shrink-0 mt-1" />}
            <div className="flex-1 min-w-[240px]">
              <h2 className="font-heading text-xl font-semibold">
                {channelOn ? "E-posta Kanalı Aktif" : "E-posta Kanalı Beklemede"}
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                {channelOn
                  ? "Bildirimler anında gönderiliyor."
                  : "DNS aktivasyonu bekleniyor. Yeni bildirimler kuyruğa alınıyor — hiçbiri kaybolmuyor."}
              </p>
            </div>
            {pendingQueue.length > 0 && channelOn && (
              <Button onClick={replayQueue} disabled={replaying} data-testid="replay-queue-btn">
                <Zap className="w-4 h-4 mr-2" /> Kuyruğu Gönder ({pendingQueue.length})
              </Button>
            )}
          </div>

          {/* Sub-channels */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
            <div className="flex items-start gap-3 p-3 bg-background/60 rounded-lg border border-border/40">
              <Server className={`w-5 h-5 shrink-0 mt-0.5 ${smtpReady ? "text-green-600" : "text-muted-foreground"}`} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">İç Bildirimler • Gmail SMTP</p>
                <p className="text-xs text-muted-foreground truncate">
                  {smtpReady ? (
                    <>Hazır: <span className="font-mono">{channel.smtp.user}</span></>
                  ) : (
                    "Kurulmadı (App Password bekleniyor)"
                  )}
                </p>
              </div>
              {smtpReady && <Badge variant="outline" className="bg-green-100 text-green-700 border-green-300 shrink-0">Hazır</Badge>}
            </div>

            <div className="flex items-start gap-3 p-3 bg-background/60 rounded-lg border border-border/40">
              <Cloud className={`w-5 h-5 shrink-0 mt-0.5 ${resendReady ? "text-green-600" : "text-muted-foreground"}`} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">Dış Bildirimler • Resend</p>
                <p className="text-xs text-muted-foreground truncate">
                  {resendReady ? (
                    <>Hazır: <span className="font-mono">{channel.resend.sender}</span></>
                  ) : channel?.resend?.is_sandbox ? (
                    "Sandbox modunda (domain verify gerekli)"
                  ) : (
                    "Yapılandırılmadı"
                  )}
                </p>
              </div>
              {resendReady && <Badge variant="outline" className="bg-green-100 text-green-700 border-green-300 shrink-0">Hazır</Badge>}
              {!resendReady && channel?.resend?.is_sandbox && <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300 shrink-0">Sandbox</Badge>}
            </div>
          </div>

          <div className="flex items-center justify-between gap-3 pt-2 border-t border-border/40 flex-wrap">
            <span className="text-sm">
              <span className="text-muted-foreground">Global E-posta Bildirimleri:</span>{" "}
              <span className="font-medium">{settings?.email_enabled ? "Açık" : "Kapalı"}</span>
            </span>
            <div className="flex items-center gap-2">
              <Switch checked={settings?.email_enabled ?? true} onCheckedChange={toggleEmail} data-testid="email-toggle" />
              <Button onClick={sendTest} disabled={sending || !channelOn || recipients.length + (channel?.internal_recipients?.length || 0) === 0} size="sm" variant="outline" data-testid="send-test-btn">
                <Send className="w-3.5 h-3.5 mr-1" /> {sending ? "..." : "Test Gönder"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pending Queue */}
      <Card data-testid="pending-queue-card">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Inbox className="w-5 h-5 text-primary" />
              <h3 className="font-heading text-lg font-semibold">Kuyruk</h3>
              <Badge variant="secondary">{pendingQueue.length}</Badge>
            </div>
            {pendingQueue.length > 0 && (
              <Button variant="ghost" size="sm" onClick={clearQueue} className="text-destructive" data-testid="clear-queue-btn">
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Tümünü Sil
              </Button>
            )}
          </div>

          {pendingQueue.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm">
              <CheckCircle2 className="w-8 h-8 mx-auto mb-2 opacity-40" />
              Kuyrukta bekleyen bildirim yok
            </div>
          ) : (
            <>
              {(() => {
                const permCount = pendingQueue.filter(i => (i.retry_count || 0) >= 3).length;
                const retryableCount = pendingQueue.length - permCount;
                return (
                  <div className="text-xs text-muted-foreground bg-muted/40 rounded-lg p-2.5 border border-border/40">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span>🔄 <strong>{retryableCount}</strong> tekrar denenebilir</span>
                      {permCount > 0 && <span>❌ <strong>{permCount}</strong> kalıcı hata (3 kere denendi)</span>}
                    </div>
                    <p className="mt-1 text-[11px] opacity-80">
                      Kota aşıldığında veya bir kanal başarısız olduğunda e-postalar burada birikir.
                      Ertesi gün (kota reset) &quot;Kuyruğu Gönder&quot; ile toplu tekrar dene.
                    </p>
                  </div>
                );
              })()}
            <div className="space-y-2">
              {pendingQueue.map((item, idx) => {
                const retryCount = item.retry_count || 0;
                const isPermanent = retryCount >= 3;
                const reasonMap = {
                  kota_asildi: { label: "Kota Aşıldı", cls: "bg-amber-100 text-amber-800 border-amber-200" },
                  kimlik_dogrulama_hatasi: { label: "Kimlik Doğrulama", cls: "bg-red-100 text-red-800 border-red-200" },
                  smtp_reddedildi: { label: "SMTP Reddedildi", cls: "bg-red-100 text-red-800 border-red-200" },
                  alan_adi_dogrulanmamis: { label: "Domain Doğrulanmamış", cls: "bg-red-100 text-red-800 border-red-200" },
                  gecersiz_alici_adresi: { label: "Geçersiz Alıcı", cls: "bg-slate-100 text-slate-700 border-slate-200" },
                  aglar_hatasi: { label: "Ağ Hatası", cls: "bg-blue-100 text-blue-800 border-blue-200" },
                  kanal_yapilandirilmamis: { label: "Kanal Yok", cls: "bg-slate-100 text-slate-700 border-slate-200" },
                  genel_hata: { label: "Genel Hata", cls: "bg-slate-100 text-slate-700 border-slate-200" },
                };
                const reason = reasonMap[item.failure_reason] || (item.failure_reason ? { label: item.failure_reason, cls: "bg-slate-100 text-slate-700 border-slate-200" } : null);
                const labelBadges = {
                  donor_thanks: "Bağışçı Teşekkür",
                  application_received: "Yeni Başvuru",
                  guardian_welcome: "Koruyucu Aile",
                  application_status: "Başvuru Statüsü",
                  adoption_followup: "Sahiplendirme Takip",
                  internal_notification: "İç Bildirim",
                };
                const labelKey = (item.log_label || "").split("_").slice(0, 2).join("_");
                const labelText = labelBadges[item.log_label] || labelBadges[labelKey] || item.log_label || "Bildirim";
                return (
                <div
                  key={idx}
                  className={`border rounded-lg ${isPermanent ? "bg-red-50/40 border-red-200" : "bg-background border-border/50"}`}
                  data-testid={`pending-item-${idx}`}
                >
                  <div className="p-3 space-y-2">
                    <div className="flex items-start gap-3 flex-wrap">
                      <div className="flex-1 min-w-[240px]">
                        <p className="text-sm font-medium">{item.subject}</p>
                        <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary border border-primary/20">
                            {labelText}
                          </span>
                          {reason && (
                            <span className={`text-[10px] px-1.5 py-0.5 rounded border ${reason.cls}`}>
                              {reason.label}
                            </span>
                          )}
                          <span className={`text-[10px] px-1.5 py-0.5 rounded border ${isPermanent ? "bg-red-100 text-red-700 border-red-200" : "bg-muted text-muted-foreground border-border/50"}`}>
                            🔁 {retryCount}/3 {isPermanent && "· Kalıcı"}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1.5 flex items-center gap-1.5 flex-wrap">
                          <Clock className="w-3 h-3" />
                          {item.queued_at ? new Date(item.queued_at).toLocaleString("tr-TR") : "-"}
                          <span>•</span>
                          <Mail className="w-3 h-3" />
                          {(item.recipients || []).length} alıcı
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="ghost" onClick={() => setExpandedIdx(expandedIdx === idx ? null : idx)} data-testid={`expand-pending-${idx}`}>
                          {expandedIdx === idx ? "Gizle" : "Detay"}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => sendPendingItem(idx)}
                          disabled={!channelOn || isPermanent}
                          data-testid={`send-pending-${idx}`}
                          title={isPermanent ? "Kalıcı hata — 3 kere denendi, önce alıcıyı kontrol et" : (channelOn ? "Şimdi tekrar dene" : "Kanal kapalı")}
                        >
                          <Send className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => deletePendingItem(idx, item.subject)} className="text-destructive" data-testid={`delete-pending-${idx}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                  {expandedIdx === idx && (
                    <div className="px-3 pb-3 space-y-2 border-t border-border/40 pt-3">
                      <div className="text-xs">
                        <span className="text-muted-foreground">Alıcılar:</span>{" "}
                        <span className="font-mono">{(item.recipients || []).join(", ")}</span>
                      </div>
                      {item.source_error && (
                        <div className="text-xs">
                          <span className="text-muted-foreground">Hata Detayı:</span>{" "}
                          <span className="font-mono text-red-700 break-all">{item.source_error}</span>
                        </div>
                      )}
                      <div className="max-h-64 overflow-auto border border-border/40 rounded bg-muted/20 p-2">
                        <iframe
                          srcDoc={item.html}
                          className="w-full h-56 bg-white rounded"
                          sandbox=""
                          title={item.subject}
                        />
                      </div>
                    </div>
                  )}
                </div>
                );
              })}
            </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Recipients */}
      <Card data-testid="recipients-card">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-primary" />
            <h3 className="font-heading text-lg font-semibold">Alıcılar</h3>
          </div>

          {/* Fixed internal recipients */}
          {(channel?.internal_recipients || []).length > 0 && (
            <div className="text-xs text-muted-foreground border-l-2 border-primary/40 pl-3 py-1">
              <p className="font-medium text-foreground mb-1">Sabit (env dosyasından)</p>
              <p className="font-mono">{channel.internal_recipients.join(", ")}</p>
            </div>
          )}

          {/* Add email */}
          <div className="flex gap-2">
            <Input
              type="email"
              placeholder="yeni@email.com"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addEmail()}
              className="flex-1"
              data-testid="add-email-input"
            />
            <Button onClick={addEmail} disabled={adding || !newEmail.trim()} size="sm" data-testid="add-email-btn">
              <Plus className="w-4 h-4 mr-1" /> Ekle
            </Button>
          </div>

          {recipients.length === 0 ? (
            <div className="text-center py-4 text-muted-foreground text-sm">Dinamik alıcı yok (sadece sabit alıcılar kullanılacak)</div>
          ) : (
            <div className="space-y-1">
              {recipients.map((email) => (
                <div key={email} className="flex items-center justify-between p-2 bg-muted/30 rounded-lg">
                  <span className="text-sm font-mono">{email}</span>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => removeEmail(email)} data-testid={`remove-email-${email}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Email Templates management */}
      <NotificationTemplatesPanel defaultTestEmail={settings?.email_recipients?.[0] || ""} />

      {/* Recent Activity */}
      {logs.length > 0 && (
        <Card data-testid="log-card">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-primary" />
                <h3 className="font-heading text-lg font-semibold">Son Aktiviteler</h3>
              </div>
              <Button variant="ghost" size="sm" onClick={fetchAll} data-testid="refresh-log-btn">
                <RefreshCw className="w-3.5 h-3.5 mr-1" /> Yenile
              </Button>
            </div>
            <div className="space-y-1">
              {[...logs].reverse().slice(0, 20).map((log, i) => {
                const ok = log.status === "sent" || log.status === "queued";
                return (
                  <div key={i} className="flex items-center gap-2 p-2 text-sm rounded hover:bg-muted/40 flex-wrap">
                    {ok ? <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                        : log.status === "failed" ? <XCircle className="w-4 h-4 text-destructive shrink-0" />
                        : <Clock className="w-4 h-4 text-muted-foreground shrink-0" />}
                    <span className="text-xs font-mono text-muted-foreground">{new Date(log.timestamp).toLocaleString("tr-TR")}</span>
                    <span className="text-xs">•</span>
                    <span className="text-sm">{typeLabel(log.type)}</span>
                    {log.subject && <span className="text-xs text-muted-foreground truncate flex-1 min-w-0">{log.subject}</span>}
                    {log.channel && <Badge variant="outline" className="text-[10px] ml-auto">{log.channel}</Badge>}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
